import type {Mapper} from '@automapper/core';
import {type DownloadColumn, type IDownloadByLinkService, ServerResponseStatus} from '@igp/shared/api';
import {map} from '@igp/shared/otel';
import {
    type DownloadStrategyRequest,
    type DownloadStrategyResponse,
    EntityDownloadClientStrategy,
    type IDownloadStrategy,
} from '@igp/shared/redux/download';
import {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import {type Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {countryLevelPartnerReportMappingKeys} from './mapper';
import type {CountryLevelPartnerReportService} from './service';
import {
    type CountryLevelPartnerReportDownloadFields,
    CountryLevelPartnerReportServiceTypes,
    type CountryLevelPartnerReportViewModelKeys,
} from './types';

@injectable()
export class CountryLevelPartnerReportDownloadClientStrategy extends EntityDownloadClientStrategy<
    CountryLevelPartnerReportViewModelKeys,
    EntityType
> {
    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(CountryLevelPartnerReportServiceTypes.service) service: CountryLevelPartnerReportService
    ) {
        super(mapper, service, EntityType.CountryLevelPartnerReport, {
            viewModelKeys: countryLevelPartnerReportMappingKeys.viewModelsKeys,
            queryFields: countryLevelPartnerReportMappingKeys.queryItemsKeys,
            summaryQueryFields: countryLevelPartnerReportMappingKeys.querySummaryKeys,
            model: countryLevelPartnerReportMappingKeys.model,
            downloadModel: countryLevelPartnerReportMappingKeys.downloadModel,
        });
    }
}

@injectable()
export class CountryLevelPartnerReportDownloadServerStrategy implements IDownloadStrategy<CountryLevelPartnerReportViewModelKeys> {
    private readonly _mapper: Mapper;
    private readonly _service: IDownloadByLinkService<CountryLevelPartnerReportDownloadFields>;

    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(CountryLevelPartnerReportServiceTypes.service)
        service: IDownloadByLinkService<CountryLevelPartnerReportDownloadFields>
    ) {
        this._mapper = mapper;
        this._service = service;
    }

    process(
        request: DownloadStrategyRequest<CountryLevelPartnerReportViewModelKeys>
    ): Observable<DownloadStrategyResponse<CountryLevelPartnerReportViewModelKeys>> {
        const columns: DownloadColumn<CountryLevelPartnerReportDownloadFields>[] = request.keys?.map<
            DownloadColumn<CountryLevelPartnerReportDownloadFields>
        >(k => {
            const queryField = this._mapper.map<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportDownloadFields>(
                k,
                countryLevelPartnerReportMappingKeys.viewModelsKeys,
                countryLevelPartnerReportMappingKeys.downloadFields
            );
            return {
                name: request.headers[k] ?? k,
                field: queryField,
            };
        });
        return this._service
            .getDownloadLink({
                filter: request.filter,
                columns,
                report_name: request.filename,
            })
            .pipe(
                map(response => ({
                    status: response?.status === ServerResponseStatus.Success ? 'Success' : 'Failed',
                    link: response?.responsePayload,
                    mode: 'server',
                }))
            );
    }
}
